import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ExportData from "../../components/common/ExportData";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import SortableTableHeader from "../../components/Table/SortableTableHeader";

const AuthorizeInterbranch = () => {
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transferList, setTransferList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchTransferList = async (page_size, search_query, current_page, sort_by, sort_order) => {
    setLoading(true);
    try {
      const sortParam = sort_by ? `&sort_by=${sort_by}&sort_order=${sort_order}` : "";
      const data = await requestWithAuth(
        `/inventory/interbranch?page=${current_page}&pageSize=${page_size}&search=${search_query}${sortParam}`,
        "GET"
      );
      setTransferList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      toast.error("Error fetching list");
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (search_query, sort_by, sort_order) => {
    setLoading(true);
    try {
      const sortParam = sort_by ? `&sort_by=${sort_by}&sort_order=${sort_order}` : "";
      const data = await requestWithAuth(
        `/inventory/interbranch?paginated=false&search=${search_query}${sortParam}`,
        "GET"
      );
      setTableData(data?.results);
    } catch (error) {
      toast.error("Error fetching list");
    } finally {
      setLoading(false);
    }
  };

  const deleteInterbranch = (interbranch_id) => {
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to delete this transfer?",
      showDenyButton: true,
      confirmButtonText: "Yes Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch/${interbranch_id}/`,
            "DELETE"
          );
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Interbranch transfer deleted successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          toast.error("Something unexpected happened.");
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const approveInterbranch = (interbranch_id, action) => {
    Swal.fire({
      icon: "question",
      title: `Are you sure you want to ${action} this transfer?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setUpdating(true);
        const body = JSON.stringify({
          status: "Approved",
        });
        try {
          const data = await requestWithAuth(
            `/inventory/interbranch/${interbranch_id}/?action=${action}`,
            "PUT",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          Swal.fire({
            icon: "success",
            title: `${action}d!`,
            text: `Interbranch transfer ${action}d successfully`,
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
          if (error.response.data.error) {
            for (const property in error.response.data.error) {
              toast.error(
                `${property} error: ${error.response.data.error[property]}`
              );
            }
          }
          if (error.response.status === 500) {
            toast.error("Something went wrong!");
          }
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const filterTransferList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortBy === key) { setSortOrder((prev) => (prev === "asc" ? "desc" : "asc")); }
    else { setSortBy(key); setSortOrder("desc"); }
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3;

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, idx) => (
      <button
        key={idx}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`min-w-[36px] h-[36px] flex items-center justify-center rounded-lg text-sm font-medium transition-all
          ${number === currentPage
            ? "bg-blue-500 text-white shadow-sm"
            : number === "..."
              ? "cursor-default text-gray-400"
              : "text-gray-600 hover:bg-gray-100"
          }`}
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchTransferList(pageSize, search, currentPage, sortBy, sortOrder);
  }, [search, currentPage, pageSize, sortBy, sortOrder]);
  useEffect(() => {
    getTableData(search, sortBy, sortOrder);
  }, [search, sortBy, sortOrder])

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">Authorization</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">
                Pending Interbranch Transfers
              </h1>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <ExportData
                  tableData={tableData}
                  fileName="Pending Interbranch Transfer"
                />
                <span className="text-sm text-gray-600">Show</span>
                <select
                  onChange={(e) => setPageSize(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  value={pageSize}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>

              <DebouncedSearchInput
                onSearch={filterTransferList}
                placeholder="Search transfers..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading transfers...</span>
                </div>
              </div>
            ) : transferList?.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <SortableTableHeader label="Source Branch" fieldKey="branch" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Destination Branch" fieldKey="destination_branch" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Quantity" fieldKey="quantity" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Total Cost" fieldKey="total_cost" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Total Items" fieldKey="total_items" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Created By" fieldKey="recorded_by" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableTableHeader label="Created At" fieldKey="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transferList?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {item?.branch}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.destination_branch}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.quantity}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.total_cost}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.total_items}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.recorded_by}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {item?.created_at}
                      </td>
                      <td className="py-4 px-6">
                        {updating ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                            <span className="text-sm text-gray-500">
                              Processing...
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {((activeSession?.user?.role === "superadmin") || (item?.branch !== activeSession?.branch?.name)) && (
                              <>
                                <button
                                  onClick={() => approveInterbranch(item?.id, "approve")}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </button>
                                <button
                                  onClick={() => approveInterbranch(item?.id, "reject")}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                  <XCircle size={14} />
                                  Reject
                                </button>
                              </>
                            )}
                            <Link
                              to={`/inventory/interbranch/view/${item?.id}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Eye size={14} />
                              View
                            </Link>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">
                  No pending transfers found
                </p>
                {search && (
                  <p className="text-gray-400 text-xs mt-1">
                    Try adjusting your search terms
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pagination */}
          {transferList?.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-all ${currentPage > 1
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-all ${currentPage < totalPages
                      ? "text-gray-600 hover:bg-gray-100"
                      : "text-gray-300 cursor-not-allowed"
                      }`}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthorizeInterbranch;
